import {
	SHAKE_DELTA_THRESHOLD,
	SHAKE_DRAW_COOLDOWN_MS,
	SHAKE_MIN_DURATION_MS,
	SHAKE_PEAK_MIN_GAP_MS,
	SHAKE_PEAK_WINDOW_MS,
	SHAKE_PEAKS_REQUIRED,
	SHAKE_SAMPLE_INTERVAL_MS
} from './shake-motion-config.js'

function detectBrowserEnv() {
	const ua = typeof navigator !== 'undefined' ? navigator.userAgent || '' : ''
	return {
		isWeChat: /MicroMessenger/i.test(ua),
		isIOS: /iPhone|iPad|iPod/i.test(ua),
		isAndroid: /Android/i.test(ua),
		isHarmony: /HarmonyOS|OpenHarmony/i.test(ua),
		isSafari: /Safari/i.test(ua) && !/Chrome|CriOS|FxiOS|EdgiOS/i.test(ua),
		isStandalone:
			typeof window !== 'undefined' &&
			(!!window.navigator.standalone || window.matchMedia('(display-mode: standalone)').matches)
	}
}

export function collectShakeDeviceInfo() {
	if (typeof window === 'undefined' || typeof navigator === 'undefined') {
		return { supported: false, reason: '非浏览器环境' }
	}
	const ua = navigator.userAgent || ''
	const env = detectBrowserEnv()
	const vv = window.visualViewport
	const screenObj = window.screen || {}
	const orientation =
		screenObj.orientation && screenObj.orientation.type
			? screenObj.orientation.type
			: typeof window.orientation === 'number'
				? String(window.orientation)
				: 'unknown'
	const DM = window.DeviceMotionEvent
	const DO = window.DeviceOrientationEvent
	const motionPermissionType =
		DM && typeof DM.requestPermission === 'function' ? 'ios-requestPermission' : 'auto-grant'
	const connection = navigator.connection || navigator.mozConnection || navigator.webkitConnection

	return {
		collectedAt: new Date().toLocaleString(),
		userAgent: ua,
		platform: navigator.platform || '',
		vendor: navigator.vendor || '',
		language: navigator.language || '',
		maxTouchPoints: navigator.maxTouchPoints != null ? navigator.maxTouchPoints : null,
		hardwareConcurrency: navigator.hardwareConcurrency != null ? navigator.hardwareConcurrency : null,
		isSecureContext: !!window.isSecureContext,
		protocol: window.location.protocol,
		host: window.location.host,
		pathname: window.location.pathname,
		hash: window.location.hash,
		env,
		screen: {
			width: screenObj.width,
			height: screenObj.height,
			availWidth: screenObj.availWidth,
			availHeight: screenObj.availHeight,
			colorDepth: screenObj.colorDepth,
			pixelDepth: screenObj.pixelDepth,
			orientation
		},
		windowSize: {
			innerWidth: window.innerWidth,
			innerHeight: window.innerHeight,
			outerWidth: window.outerWidth,
			outerHeight: window.outerHeight,
			devicePixelRatio: window.devicePixelRatio || 1
		},
		visualViewport: vv
			? {
					width: vv.width,
					height: vv.height,
					scale: vv.scale,
					offsetLeft: vv.offsetLeft,
					offsetTop: vv.offsetTop
				}
			: null,
		motion: {
			deviceMotionSupported: !!DM,
			deviceOrientationSupported: !!DO,
			motionPermissionType,
			accelerometerPermissionApi:
				typeof navigator.permissions !== 'undefined' &&
				typeof navigator.permissions.query === 'function'
		},
		network: connection
			? {
					effectiveType: connection.effectiveType || '',
					downlink: connection.downlink,
					rtt: connection.rtt,
					saveData: connection.saveData
				}
			: null
	}
}

export function diagnoseShakeEnvironment(info, runtime) {
	const issues = []
	const hints = []
	if (!info.motion.deviceMotionSupported) {
		issues.push('当前浏览器不支持 DeviceMotionEvent，无法摇一摇')
	}
	if (!info.isSecureContext && info.protocol !== 'file:') {
		issues.push('非 HTTPS 安全上下文，部分浏览器会禁用运动传感器')
		hints.push('请使用 https:// 访问本页')
	}
	if (info.env.isIOS && info.motion.motionPermissionType === 'ios-requestPermission') {
		if (runtime.permissionStatus !== 'granted') {
			issues.push('iOS 需要用户手势授权运动传感器（DeviceMotionEvent.requestPermission）')
			hints.push('点击下方「授权运动传感器」并在系统弹窗中选择允许')
		}
	}
	if (info.env.isWeChat) {
		hints.push('微信内置浏览器：若摇一摇无效，可尝试用系统 Safari/Chrome 打开同一链接对比')
	}
	if (runtime.listenerActive && runtime.sampleCount > 0 && runtime.nullAccelCount === runtime.sampleCount) {
		issues.push('已收到 devicemotion 事件，但加速度数据始终为空')
		hints.push('可能是权限未真正生效，或当前 WebView 未开放传感器')
	}
	if (
		runtime.listenerActive &&
		runtime.sampleCount > 20 &&
		runtime.maxDelta > 0 &&
		runtime.maxDelta < SHAKE_DELTA_THRESHOLD
	) {
		issues.push(
			'传感器有数据，但晃动强度未达阈值 ' +
				SHAKE_DELTA_THRESHOLD +
				'（当前最大 delta=' +
				runtime.maxDelta.toFixed(2) +
				'）'
		)
		hints.push('请加大晃动幅度或更换握持方式再试')
	}
	if (runtime.triggerCount === 0 && runtime.peakCount >= SHAKE_PEAKS_REQUIRED && !runtime.durationOk) {
		hints.push('峰值次数已够，但持续时长不足 ' + SHAKE_MIN_DURATION_MS + 'ms，请连续多摇一会儿')
	}
	if (!issues.length && runtime.permissionStatus === 'granted' && runtime.listenerActive) {
		hints.push('环境正常，请晃动手机观察 delta 与峰值是否达到门槛')
	}
	return { issues, hints }
}

function registerShakePeak(peakTimes, sessionStartRef, now) {
	const last = peakTimes[peakTimes.length - 1]
	if (last != null && now - last < SHAKE_PEAK_MIN_GAP_MS) {
		return isShakeReady(peakTimes, sessionStartRef.value, now)
	}
	const cutoff = now - SHAKE_PEAK_WINDOW_MS
	const next = peakTimes.filter(function (t) {
		return t >= cutoff
	})
	next.push(now)
	peakTimes.length = 0
	Array.prototype.push.apply(peakTimes, next)
	if (peakTimes.length === 1) {
		sessionStartRef.value = now
	}
	return isShakeReady(peakTimes, sessionStartRef.value, now)
}

function isShakeReady(peakTimes, sessionStart, now) {
	if (peakTimes.length < SHAKE_PEAKS_REQUIRED) return false
	if (!sessionStart) return false
	return now - sessionStart >= SHAKE_MIN_DURATION_MS
}

export function createShakeDetectSession(handlers) {
	const h = handlers || {}
	let motionHandler = null
	let permissionStatus = 'unknown'
	let permissionRequesting = false
	let listenerActive = false
	let motionLast = { x: null, y: null, z: null, t: 0 }
	let peakTimes = []
	const sessionStartRef = { value: 0 }
	let lastDrawAt = 0
	let triggerCount = 0
	let sampleCount = 0
	let nullAccelCount = 0
	let maxDelta = 0
	let current = { x: 0, y: 0, z: 0, delta: 0, accSource: '' }
	const logs = []
	const maxLogs = 30

	function pushLog(type, detail) {
		const item = {
			time: new Date().toLocaleTimeString(),
			type,
			detail
		}
		logs.unshift(item)
		if (logs.length > maxLogs) logs.length = maxLogs
		if (typeof h.onLog === 'function') h.onLog(logs.slice())
	}

	function resetSession() {
		motionLast = { x: null, y: null, z: null, t: 0 }
		peakTimes = []
		sessionStartRef.value = 0
		current.delta = 0
		pushLog('reset', '已重置峰值统计')
		if (typeof h.onUpdate === 'function') h.onUpdate(getRuntime())
	}

	function processMotion(x, y, z, accSource) {
		const now = Date.now()
		current = { x, y, z, delta: current.delta, accSource }
		if (now - motionLast.t < SHAKE_SAMPLE_INTERVAL_MS) return
		if (motionLast.x != null) {
			const dx = x - motionLast.x
			const dy = y - motionLast.y
			const dz = z - motionLast.z
			const delta = Math.abs(dx) + Math.abs(dy) + Math.abs(dz)
			current.delta = delta
			if (delta > maxDelta) maxDelta = delta
			const peakReady = delta >= SHAKE_DELTA_THRESHOLD && registerShakePeak(peakTimes, sessionStartRef, now)
			if (delta >= SHAKE_DELTA_THRESHOLD) {
				pushLog('peak', 'delta=' + delta.toFixed(2) + '，累计峰值 ' + peakTimes.length + ' 次')
			}
			if (peakReady && now - lastDrawAt > SHAKE_DRAW_COOLDOWN_MS) {
				lastDrawAt = now
				triggerCount += 1
				peakTimes = []
				sessionStartRef.value = 0
				pushLog('trigger', '达到摇一摇抽奖门槛（第 ' + triggerCount + ' 次）')
				if (typeof h.onTrigger === 'function') h.onTrigger(getRuntime())
			}
		}
		motionLast = { x, y, z, t: now }
		if (typeof h.onUpdate === 'function') h.onUpdate(getRuntime())
	}

	function onDeviceMotion(event) {
		sampleCount += 1
		const accGravity = event && event.accelerationIncludingGravity
		const acc = event && event.acceleration
		let pick = accGravity || acc
		let accSource = accGravity ? 'accelerationIncludingGravity' : acc ? 'acceleration' : ''
		if (!pick || (pick.x == null && pick.y == null && pick.z == null)) {
			nullAccelCount += 1
			if (acc && accGravity && acc !== accGravity) {
				pick = acc
				accSource = 'acceleration(fallback)'
			}
		}
		if (!pick) return
		const x = pick.x != null ? pick.x : 0
		const y = pick.y != null ? pick.y : 0
		const z = pick.z != null ? pick.z : 0
		processMotion(x, y, z, accSource)
	}

	function syncListener() {
		stopListener()
		if (permissionStatus !== 'granted') return
		const DM = window.DeviceMotionEvent
		if (!DM) return
		motionHandler = onDeviceMotion
		window.addEventListener('devicemotion', motionHandler, false)
		listenerActive = true
		pushLog('listen', '已开始监听 devicemotion')
		if (typeof h.onUpdate === 'function') h.onUpdate(getRuntime())
	}

	function stopListener() {
		if (motionHandler) {
			window.removeEventListener('devicemotion', motionHandler, false)
			motionHandler = null
		}
		listenerActive = false
	}

	function getRuntime() {
		const now = Date.now()
		const durationMs = sessionStartRef.value ? now - sessionStartRef.value : 0
		return {
			permissionStatus,
			permissionRequesting,
			listenerActive,
			sampleCount,
			nullAccelCount,
			maxDelta,
			triggerCount,
			current,
			peakCount: peakTimes.length,
			peakTimes: peakTimes.slice(),
			sessionDurationMs: durationMs,
			durationOk: durationMs >= SHAKE_MIN_DURATION_MS,
			peaksOk: peakTimes.length >= SHAKE_PEAKS_REQUIRED,
			deltaOk: current.delta >= SHAKE_DELTA_THRESHOLD,
			readyNow: isShakeReady(peakTimes, sessionStartRef.value, now),
			logs: logs.slice()
		}
	}

	return {
		getRuntime,
		getLogs: function () {
			return logs.slice()
		},
		resetSession,
		requestPermission: function () {
			if (permissionRequesting || permissionStatus === 'granted') {
				syncListener()
				return Promise.resolve(permissionStatus)
			}
			const DM = window.DeviceMotionEvent
			if (!DM) {
				permissionStatus = 'unsupported'
				pushLog('error', '不支持 DeviceMotionEvent')
				return Promise.resolve(permissionStatus)
			}
			if (typeof DM.requestPermission !== 'function') {
				permissionStatus = 'granted'
				pushLog('permission', '无需 requestPermission，已自动启用')
				syncListener()
				return Promise.resolve(permissionStatus)
			}
			permissionRequesting = true
			pushLog('permission', '正在请求 iOS 运动传感器权限…')
			return DM.requestPermission()
				.then(function (result) {
					permissionStatus = result === 'granted' ? 'granted' : 'denied'
					pushLog('permission', '授权结果：' + result)
					if (permissionStatus === 'granted') syncListener()
					return permissionStatus
				})
				.catch(function (err) {
					permissionStatus = 'denied'
					pushLog('error', (err && err.message) || '授权失败')
					return permissionStatus
				})
				.finally(function () {
					permissionRequesting = false
					if (typeof h.onUpdate === 'function') h.onUpdate(getRuntime())
				})
		},
		start: syncListener,
		stop: function () {
			stopListener()
			if (typeof h.onUpdate === 'function') h.onUpdate(getRuntime())
		},
		destroy: function () {
			stopListener()
		}
	}
}
